import { computed, ref, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useRoute, useRouter } from 'vue-router';
import { api } from './api';

/**
 * Which language the back office is being read in.  design.md §17.1
 *
 * IT IS ONE CHOICE, NOT ONE PER SCREEN. Four views each read `?lang=` and each
 * defaulted to Finnish on their own, and there was no control anywhere that set
 * it — you edited the address bar. So the catalogue showed a product called
 * "Rengas" in a category called "chassis", and the only way to see either in
 * English was to know the query parameter existed.
 *
 * THE URL STILL WINS WHERE IT IS PRESENT. §13.1 wants a screen to survive being
 * sent to a colleague, and a link that changes meaning depending on the
 * recipient's preference is not that. So `?lang=` overrides the preference, and
 * changing the preference rewrites a `?lang=` that is already there — otherwise
 * the picker would silently do nothing on exactly the screens that use it most.
 *
 * The preference outlives the tab. It is a way of reading, like a font size,
 * not a filter to be cleared.
 */

const STORAGE_KEY = 'lasti.language';

/**
 * Read once at module load, then kept in memory.
 *
 * `localStorage` throws in a locked-down browser rather than returning null, so
 * the read is guarded: a preference that cannot be stored is a worse screen,
 * not a broken one.
 */
function stored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

const preference = ref<string | null>(stored());

export function setPreferredLanguage(code: string): void {
  preference.value = code;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Chosen for this session only. Better than refusing to switch.
  }
}

// --- the summary query, shared -------------------------------------------------

/** One query for the language list, keyed so every caller shares the cache. */
function useQuerySummary() {
  return useQuery({ queryKey: ['catalog', 'summary'], queryFn: () => api.summary() });
}

/**
 * Which of the three answers wins, in order.
 *
 * A link that names a language means it; a person who chose one means it
 * everywhere else; and a tenant that has neither is read in the language it
 * writes in. Pure, because this is the rule the whole back office agrees on and
 * it should be checkable without a browser.
 */
export function resolveLanguage(
  fromUrl: string | null,
  preferred: string | null,
  tenantDefault: string,
): string {
  return fromUrl || preferred || tenantDefault;
}

/** The tenant's own default, which is what to fall back to before anything is chosen. */
export function useLanguages() {
  const summary = useQuerySummary();
  const languages = computed(() =>
    (summary.data.value?.translationCoverage ?? []).map((row) => ({
      code: row.languageCode,
      isDefault: row.isDefault,
      translated: row.translated,
      total: row.total,
    })),
  );
  const fallback = computed(
    () => languages.value.find((l) => l.isDefault)?.code ?? languages.value[0]?.code ?? 'fi',
  );
  return { languages, fallback };
}

/**
 * The language this screen is being read in, and the way to change it.
 *
 * `lang` is what every query key and every API call should use. Nothing should
 * read `route.query.lang` directly any more — that is how four views came to
 * disagree about what the default was.
 */
export function useLanguage(): {
  lang: ComputedRef<string>;
  languages: ReturnType<typeof useLanguages>['languages'];
  choose: (code: string) => void;
} {
  const route = useRoute();
  const router = useRouter();
  const { languages, fallback } = useLanguages();

  const lang = computed(() =>
    resolveLanguage(
      typeof route.query.lang === 'string' ? route.query.lang : null,
      preference.value,
      fallback.value,
    ),
  );

  function choose(code: string): void {
    setPreferredLanguage(code);
    // Only when the URL is already carrying one. Adding `?lang=` to every
    // address the moment somebody picks a language would put it in every link
    // they then share, pinning the reader to the sender's choice.
    if (typeof route.query.lang === 'string' && route.query.lang !== code) {
      void router.replace({ query: { ...route.query, lang: code } });
    }
  }

  return { lang, languages, choose };
}

/**
 * Is this value a fallback from another language?
 *
 * The back office shows what it has rather than a blank, and then says so —
 * see `TranslationFallback`. A blank cell reads as "this product has no name", which
 * is a different and much more alarming thing than "nobody has written the
 * English one yet".
 */
export interface Translated {
  text: string;
  /** The language it is actually in, when that is not the one being read. */
  fallbackFrom?: string | null;
}
